import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useAddSecondaryEmail,
  useMakePrimaryEmail,
  useRemoveSecondaryEmail,
} from "@/features/auth/hooks/use-email-challenge";
import { useUser } from "@/features/auth/hooks/use-user";
import { DotsThreeOutlineIcon, Plus } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "@/features/utils/i18n";
import { AddEmailDrawer } from "./add-email-drawer";

export const EmailTable = () => {
  const { t } = useTranslation();
  const [isAddDrawerOpen, setIsAddDrawerOpen] = useState(false);
  const { data: user } = useUser();
  const emails = [user?.email, ...(user?.additional_emails || [])].filter(
    Boolean,
  );

  const makePrimaryMutation = useMakePrimaryEmail();
  const removeEmailMutation = useRemoveSecondaryEmail();
  const addEmailMutation = useAddSecondaryEmail();

  const handleMakePrimary = async (value: string) => {
    const email = emails.find((e) => e === value);
    if (!email) return;

    makePrimaryMutation.mutate({ email });
  };

  const handleRemove = async (emailId: string) => {
    const email = emails.find((e) => e === emailId);
    if (!email) return;

    removeEmailMutation.mutate({ email });
  };

  const handleAddSuccess = async (preSignedToken: string) => {
    addEmailMutation.mutate(
      { emailChallengeToken: preSignedToken },
      {
        onSuccess: () => {
          setIsAddDrawerOpen(false);
        },
      },
    );
  };

  const isLoading =
    makePrimaryMutation.isPending ||
    removeEmailMutation.isPending ||
    addEmailMutation.isPending;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">
          {t("security.emailAddresses.description")}
        </p>
        <Button
          onClick={() => setIsAddDrawerOpen(true)}
          size="sm"
          className="flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          {t("security.emailAddresses.addEmail")}
        </Button>
      </div>

      <div className="rounded-sm border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("security.emailAddresses.title")}</TableHead>
              <TableHead className="w-[100px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {emails.map((email) => (
              <TableRow key={email}>
                <TableCell className="font-medium">{email}</TableCell>
                <TableCell>
                  {email === user?.email && (
                    <Badge variant="default">{t("common.primary")}</Badge>
                  )}
                  {email !== user?.email && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          className="h-8 w-8 p-0"
                          disabled={isLoading}
                        >
                          <span className="sr-only">
                            {t("common.openMenu")}
                          </span>
                          <DotsThreeOutlineIcon
                            weight="fill"
                            className="h-4 w-4"
                          />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => handleMakePrimary(email!)}
                          disabled={isLoading}
                        >
                          {t("common.makePrimary")}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleRemove(email!)}
                          className="text-destructive"
                          disabled={isLoading}
                        >
                          {t("common.remove")}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {emails.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={3}
                  className="text-center py-8 text-muted-foreground"
                >
                  {t("security.emailAddresses.noEmails")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <AddEmailDrawer
        open={isAddDrawerOpen}
        onOpenChange={setIsAddDrawerOpen}
        onSuccess={handleAddSuccess}
      />
    </div>
  );
};
